import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import QuoteRequestModal from '@/components/public/QuoteRequestModal';
import ScheduleCallModal from '@/components/public/ScheduleCallModal';
import { getSectionByKey } from '@/services/siteSections';

/**
 * Header/Navbar oficial del sitio público.
 * Es el ÚNICO header público del proyecto: PublicDesktopLayout y las páginas
 * que no viven dentro del layout (ej. /precios) montan este mismo componente.
 * No debe existir ninguna copia de este navbar en otro archivo.
 */
export const PUBLIC_NAV_HEIGHT = 120;

export const publicNavLinks = [
  { path: '/', label: 'Inicio' },
  { path: '/paquetes', label: 'Paquetes' },
  { path: '/productos', label: 'Productos' },
  { path: '/muestras/fotos', label: 'Fotos' },
  { path: '/muestras/video', label: 'Video' },
  { path: '/ayuda', label: 'Ayuda' },
];

export default function PublicNavbar() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    getSectionByKey('hero')
      .then((heroSection) => {
        if (cancelled) return;
        if (heroSection?.content_json?.logo) {
          setLogoUrl(heroSection.content_json.logo);
        }
      })
      .catch((error) => {
        console.error('Error loading logo:', error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const shareUrl = window.location.href;
  const shareTitle = 'Fotos y Video Santa Rosa - Capturamos tus momentos especiales';
  const shareText = 'Descubre nuestros servicios de fotografía y video profesional';

  const handleShare = (platform: string) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(shareTitle);
    const encodedText = encodeURIComponent(shareText);

    let url = '';
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
        break;
      case 'telegram':
        url = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'email':
        url = `mailto:?subject=${encodedTitle}&body=${encodedText}%20${encodedUrl}`;
        break;
      default:
        break;
    }

    if (!url) return;

    if (platform === 'email') {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'width=600,height=400');
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Fotos y Video Santa Rosa"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <i className="ri-camera-lens-fill text-2xl text-pink-600"></i>
                )}
              </div>
              <span className="text-lg font-semibold text-gray-900">Fotos y Video Santa Rosa</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {publicNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    isActive(link.path)
                      ? 'text-pink-600'
                      : 'text-gray-600 hover:text-pink-600'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => setQuoteModalOpen(true)}
                className="px-4 py-2 text-sm font-medium text-pink-600 hover:text-pink-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cotizar
              </button>
              <button
                onClick={() => setScheduleModalOpen(true)}
                className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg hover:from-pink-700 hover:to-purple-700 transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
              >
                Agendar Llamada
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:text-gray-900 cursor-pointer"
              aria-label="Abrir menú"
              aria-expanded={mobileMenuOpen}
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-100">
              {publicNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 text-sm font-medium transition-colors cursor-pointer ${
                    isActive(link.path)
                      ? 'text-pink-600 bg-pink-50'
                      : 'text-gray-600 hover:text-pink-600 hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="px-4 pt-4 space-y-2 border-t border-gray-100 mt-2">
                <button
                  onClick={() => {
                    setQuoteModalOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full px-4 py-2 text-sm font-medium text-pink-600 border border-pink-600 rounded-lg hover:bg-pink-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cotizar
                </button>
                <button
                  onClick={() => {
                    setScheduleModalOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg hover:from-pink-700 hover:to-purple-700 transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  Agendar Llamada
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Share Buttons Below Menu */}
        <div className="border-t border-gray-100 bg-gradient-to-r from-pink-50/50 to-purple-50/50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <span className="text-xs text-gray-600 mr-2 whitespace-nowrap">Compartir:</span>
              <button
                onClick={() => handleShare('facebook')}
                className="w-8 h-8 flex items-center justify-center bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-all shadow-sm cursor-pointer"
                aria-label="Compartir en Facebook"
              >
                <i className="ri-facebook-fill text-sm"></i>
              </button>
              <button
                onClick={() => handleShare('twitter')}
                className="w-8 h-8 flex items-center justify-center bg-sky-500 text-white rounded-full hover:bg-sky-600 transition-all shadow-sm cursor-pointer"
                aria-label="Compartir en Twitter"
              >
                <i className="ri-twitter-x-fill text-sm"></i>
              </button>
              <button
                onClick={() => handleShare('whatsapp')}
                className="w-8 h-8 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition-all shadow-sm cursor-pointer"
                aria-label="Compartir en WhatsApp"
              >
                <i className="ri-whatsapp-fill text-sm"></i>
              </button>
              <button
                onClick={() => handleShare('telegram')}
                className="w-8 h-8 flex items-center justify-center bg-cyan-500 text-white rounded-full hover:bg-cyan-600 transition-all shadow-sm cursor-pointer"
                aria-label="Compartir en Telegram"
              >
                <i className="ri-telegram-fill text-sm"></i>
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="w-8 h-8 flex items-center justify-center bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-all shadow-sm cursor-pointer"
                aria-label="Compartir en LinkedIn"
              >
                <i className="ri-linkedin-fill text-sm"></i>
              </button>
              <button
                onClick={() => handleShare('email')}
                className="w-8 h-8 flex items-center justify-center bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-all shadow-sm cursor-pointer"
                aria-label="Compartir por Email"
              >
                <i className="ri-mail-fill text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <QuoteRequestModal
        isOpen={quoteModalOpen}
        onClose={() => setQuoteModalOpen(false)}
      />
      <ScheduleCallModal
        isOpen={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
      />
    </>
  );
}